import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import { Box, useTheme } from "@mui/material";
import {
  focusRingSx,
  panelBorder,
  touchTargetSx,
} from "../Dashboard/overviewPanel";

interface SectionButtonProps
  extends Omit<ComponentPropsWithoutRef<"button">, "onClick" | "children"> {
  label: string;
  ariaLabel: string;
  icon?: ReactNode;
  onClick: () => void;
}

/**
 * An outlined action beside a heading: green on hover, a green glyph. Takes a
 * ref and passes other props through, so a Tooltip can wrap it directly.
 */
export const SectionButton = forwardRef<HTMLButtonElement, SectionButtonProps>(
  ({ label, ariaLabel, icon, onClick, ...rest }, ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    return (
      <Box
        {...rest}
        ref={ref}
        component="button"
        type="button"
        aria-label={ariaLabel}
        onClick={onClick}
        sx={{
          all: "unset",
          ...focusRingSx,
          ...touchTargetSx,
          boxSizing: "border-box",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
          gap: 0.5,
          height: 32,
          px: 1.25,
          borderRadius: 1.5,
          cursor: "pointer",
          fontFamily: "inherit",
          fontSize: "0.8125rem",
          fontWeight: 600,
          color: "text.primary",
          border: `1px solid ${panelBorder(isDark)}`,
          WebkitTapHighlightColor: "transparent",
          transition: "border-color 0.2s ease",
          "& .MuiSvgIcon-root": {
            fontSize: 16,
            color: isDark ? "primary.main" : "primary.dark",
          },
          "&:hover": { borderColor: "rgba(34, 197, 94, 0.45)" },
          "&:active": { transform: "scale(0.97)" },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
            "&:active": { transform: "none" },
          },
        }}
      >
        {icon}
        {label}
      </Box>
    );
  },
);

SectionButton.displayName = "SectionButton";
